import { QueryParams } from '../../types/api';
import { appConfig } from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: any,
    public timestamp: Date = new Date(),
    public statusCode?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Specific error class for version conflicts (HTTP 409)
 */
export class VersionConflictError extends ApiError {
  constructor(
    message: string,
    public entityId: string,
    public entityType: 'partner' | 'connection' | 'task',
    public currentVersion?: number
  ) {
    super('VERSION_CONFLICT', message, { entityId, entityType, currentVersion }, new Date(), 409);
    this.name = 'VersionConflictError';
  }
}

export class ApiErrorHandler {
  static handle(error: ApiError): string {
    // Convert API errors to user-friendly messages
    switch (error.code) {
      case 'NETWORK_ERROR':
        return 'Network connection failed. Please check your internet connection.';
      case 'UNAUTHORIZED':
        return 'You are not authorized to perform this action. Please sign in again.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'VALIDATION_ERROR':
        return 'Invalid data provided. Please check your input.';
      case 'SERVER_ERROR':
        return 'Server error occurred. Please try again later.';
      case 'VERSION_CONFLICT':
        return 'This resource has been modified. Please refresh and try again.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }
}

export interface RetryConfig {
  maxAttempts: number;
  backoffStrategy: 'linear' | 'exponential';
  baseDelay: number;
  maxDelay: number;
}

export class BaseApiService {
  private defaultRetryConfig: RetryConfig = {
    maxAttempts: 3,
    backoffStrategy: 'exponential',
    baseDelay: 1000,
    maxDelay: 10000
  };

  private async getBaseUrl(): Promise<string> {
    // Ensure we have a valid config before proceeding
    let retries = 0;
    const maxRetries = 10;
    
    while (!appConfig && retries < maxRetries) {
      try {
        const response = await fetch('/config.json');
        const config = await response.json();
        // Don't modify the global appConfig here, just return the endpoint
        return config.apiEndpoint || '/api';
      } catch (error) {
        console.warn(`Failed to load config (attempt ${retries + 1}):`, error);
        retries++;
        if (retries < maxRetries) {
          const backoffMs = 100 * retries;
          await new Promise(resolve => setTimeout(resolve, backoffMs)); // exponential backoff
        }
      }
    }
    
    // If we have appConfig, use it
    if (appConfig?.apiEndpoint) {
      return appConfig.apiEndpoint;
    }
    
    // Final fallback
    console.error('Could not load API endpoint configuration');
    return '/api';
  }

  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      // Try identity token first for API Gateway Cognito authorizer
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        return idToken;
      }
      
      // Fallback to access token if identity token is not available
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        throw new ApiError('UNAUTHORIZED', 'No valid authentication token found');
      }
      return accessToken;
    } catch (error) {
      throw new ApiError('UNAUTHORIZED', 'Failed to get authentication token');
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private calculateDelay(attempt: number, config: RetryConfig): number {
    if (config.backoffStrategy === 'exponential') {
      return Math.min(config.baseDelay * Math.pow(2, attempt), config.maxDelay);
    }
    return Math.min(config.baseDelay * (attempt + 1), config.maxDelay);
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retryConfig: RetryConfig = this.defaultRetryConfig
  ): Promise<Response> {
    let lastError: Error = new Error('Request failed');

    for (let attempt = 0; attempt < retryConfig.maxAttempts; attempt++) {
      try {
        const baseUrl = await this.getBaseUrl();
        const authToken = await this.getAuthToken();
        
        // Debug logging and explicit URL construction to prevent string corruption
        console.log('API Request Debug:', { baseUrl, url, method: options.method || 'GET' });
        
        // Ensure proper URL construction with explicit string handling
        // Use URL constructor to prevent string concatenation issues
        let fullUrl: string;
        try {
          // Normalize baseUrl (remove trailing slash if present)
          const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
          // Ensure url starts with slash
          const normalizedPath = url.startsWith('/') ? url : `/${url}`;
          // Construct URL safely
          fullUrl = `${normalizedBaseUrl}${normalizedPath}`;
          
          // Additional safety check using URL constructor
          const urlObj = new URL(fullUrl);
          fullUrl = urlObj.toString();
        } catch (urlError) {
          console.error('URL construction failed:', { baseUrl, url, error: urlError });
          throw new ApiError('URL_CONSTRUCTION_ERROR', `Failed to construct valid URL: ${baseUrl} + ${url}`);
        }
        
        console.log('Constructed URL:', fullUrl);
        
        // Validate URL construction
        if (fullUrl.includes('compartners')) {
          console.error('URL construction error detected:', { baseUrl, url, fullUrl });
          throw new ApiError('URL_CONSTRUCTION_ERROR', 'Invalid URL construction detected');
        }
        
        const response = await fetch(fullUrl, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            ...options.headers,
          },
        });

        // Check if the response is successful
        if (!response.ok) {
          if (response.status === 401) {
            throw new ApiError('UNAUTHORIZED', 'Authentication failed');
          }
          if (response.status === 403) {
            throw new ApiError('FORBIDDEN', 'Access denied');
          }
          // Handle version conflict (HTTP 409)
          if (response.status === 409) {
            const errorBody = await response.json().catch(() => ({}));
            if (errorBody.error === 'VersionConflict') {
              throw new VersionConflictError(
                errorBody.message || 'Resource has been modified',
                errorBody.entityId,
                errorBody.entityType,
                errorBody.currentVersion
              );
            }
            throw new ApiError('CONFLICT', errorBody.message || 'Resource conflict', errorBody, new Date(), 409);
          }
          if (response.status >= 500) {
            throw new ApiError('SERVER_ERROR', `Server error: ${response.status}`);
          }
          // Parse response body for all other errors (400, 404, 422, etc.)
          const errorBody = await response.json().catch(() => ({}));
          throw new ApiError(
            errorBody.errorCode || errorBody.error || 'API_ERROR',
            errorBody.message || `API error: ${response.status}`,
            errorBody,
            new Date(),
            response.status
          );
        }

        return response;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        // Don't retry on authentication errors, version conflicts, or resource conflicts (e.g., dependency errors)
        if (error instanceof ApiError && (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN' || error.code === 'VERSION_CONFLICT' || error.code === 'CONFLICT')) {
          throw error;
        }
        
        if (attempt < retryConfig.maxAttempts - 1) {
          const delayMs = this.calculateDelay(attempt, retryConfig);
          await this.delay(delayMs);
        }
      }
    }

    throw lastError;
  }

  async get(url: string, params?: QueryParams): Promise<Response> {
    const queryString = params ? this.buildQueryString(params) : '';
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.fetchWithRetry(fullUrl);
  }

  async post(url: string, data?: any): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(url: string, data?: any): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(url: string, data?: any): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // THUB-152: `data` is optional so a collection DELETE can carry a selection body
  // (e.g. {heldFileIds:[...]}). API Gateway REST forwards a DELETE body through the
  // Lambda proxy integration unchanged. Existing callers pass no data and are byte-for-byte
  // unaffected — `undefined` body is exactly what fetch received before.
  async delete(url: string, data?: any): Promise<Response> {
    return this.fetchWithRetry(url, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async getPaginated<T>(url: string, params?: QueryParams): Promise<T> {
    const queryString = params ? this.buildQueryString(params) : '';
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    const response = await this.fetchWithRetry(fullUrl);
    return response.json();
  }

  /**
   * Serialize QueryParams into a FLAT query string (THUB-169 step 2).
   *
   * Historically `filters`/`sort` were serialized as bracket form
   * (`filter[0][field]=status&filter[0][value]=active`). Those keys contain
   * `[`/`]`, which the backend's shared query-param gate drops (its name regex
   * is `^[a-zA-Z0-9_]+$`), so the bracket params never reached any handler and
   * list filtering happened entirely client-side. THUB-169 makes the backend
   * honor real flat params per-endpoint and (step 3) reject unknown ones, so the
   * GUI must stop emitting bracket-form keys. This method now emits flat keys
   * only: `?field=value` for filters, `?sortBy=&sortOrder=` for sort.
   *
   * Multi-value (`operator: 'in'`) filters are emitted as REPEATED flat keys
   * (`?status=active&status=disabled`) rather than a bracket array — the
   * conventional flat multi-value encoding. Backends that read a single value
   * receive the last occurrence (API Gateway semantics); the list pages also
   * filter client-side, so the rendered result stays correct either way. No
   * live caller currently passes `filters` (all use the argument-less
   * getTasks()/getConnections()), so this is a forward-compatibility change with
   * no behavior change for current traffic.
   */
  protected buildQueryString(params: QueryParams): string {
    const searchParams = new URLSearchParams();

    if (params.filters) {
      params.filters.forEach((filter) => {
        // Skip empty/undefined values so we never emit `?status=`.
        if (filter.value === undefined || filter.value === null) {
          return;
        }
        // `in` (or any array value) -> repeated flat keys: ?field=a&field=b.
        if (Array.isArray(filter.value)) {
          filter.value.forEach((v) => {
            if (v !== undefined && v !== null && v !== '') {
              searchParams.append(filter.field, String(v));
            }
          });
        } else if (filter.value !== '') {
          searchParams.append(filter.field, String(filter.value));
        }
      });
    }

    if (params.sort) {
      // Flat sort: the last sort wins (backends accept a single sortBy/sortOrder).
      // The GUI only ever sends a single sort entry.
      params.sort.forEach((sort) => {
        searchParams.set('sortBy', sort.field);
        searchParams.set('sortOrder', sort.order);
      });
    }

    if (params.pagination) {
      searchParams.append('page', String(params.pagination.page));
      searchParams.append('limit', String(params.pagination.limit));
      if (params.pagination.sortBy) {
        searchParams.set('sortBy', params.pagination.sortBy);
      }
      if (params.pagination.sortOrder) {
        searchParams.set('sortOrder', params.pagination.sortOrder);
      }
    }

    if (params.dateRange) {
      searchParams.append('startDate', params.dateRange.start.toISOString());
      searchParams.append('endDate', params.dateRange.end.toISOString());
    }

    return searchParams.toString();
  }
}

/**
 * Unwrap the standard API response envelope.
 * Backend APIs wrap responses as { success: true, data: { ...payload } }.
 * This function extracts the inner payload, handling both wrapped and flat formats.
 */
export function unwrapApiResponse<T = any>(jsonData: any): T {
  if (jsonData && typeof jsonData === 'object' && 'success' in jsonData && 'data' in jsonData) {
    return jsonData.data as T;
  }
  return jsonData as T;
}

// Create and export a singleton instance
export const apiService = new BaseApiService();